import { create } from "zustand";

import { createSlateEditor } from "@platejs/core";
import { MarkdownPlugin } from "@platejs/markdown";
import { KEYS } from "@platejs/utils";

import type {
	Note,
	NoteBlock,
	NoteContentInfo,
	NoteFolderInfo,
	NoteInfo,
	NotePopulated,
	NoteTitleInfo,
	Share,
} from "@vita-learning/types";
import {
	createManyNotes,
	createNote,
	createNoteShareUrl,
	deleteNote,
	fetchNotes,
	generateNote,
	getNote,
	modifyNoteFolder,
	modifyNoteTitle,
	shareNote,
	syncNoteContent,
	updateNote,
} from "~/shared/services";
import { partition } from "~/shared/utils";

import { plugins as editorPlugins } from "../../../components/text-editor/TextEditor";

import { notePredictionStreamingReducer } from "./reducers";

export interface NotesState {
	readonly data: NotePopulated[];
	readonly entity?: NotePopulated;
	readonly isPending: boolean;
}

interface NoteActions {
	readonly get: (id: string) => Promise<NotePopulated>;
	readonly sync: (note: NoteContentInfo) => Promise<void>;
	readonly generate: (
		note: Partial<NoteInfo>,
		source: Extract<Note["source"], "prompt" | "url" | "file">,
		onStream: (note: Note) => void
	) => Promise<NotePopulated>;
	readonly refresh: (url: string) => Promise<NotePopulated[]>;
	readonly create: (note: NoteInfo) => Promise<NotePopulated>;
	readonly createMany: (notes: NoteInfo[]) => Promise<NotePopulated[]>;
	readonly createShareUrl: (id: string) => Promise<string>;
	readonly update: (note: NoteInfo) => Promise<NotePopulated>;
	readonly share: (id: string, share: Share) => Promise<NotePopulated>;
	readonly modify: (
		note: NoteFolderInfo | NoteTitleInfo
	) => Promise<NotePopulated>;
	readonly remove: (id: string) => Promise<NotePopulated>;
	readonly edit: (id: string, cb?: (id: string) => void) => void;
	readonly reset: () => void;
}

export type NotesStore = NotesState & NoteActions;

const defaultState: NotesState = {
	data: [],
	isPending: false,
};

export const createNotesStore = (initialState: NotesState) =>
	create<NotesStore>()((set, get) => ({
		...defaultState,
		...initialState,
		create: async note => {
			try {
				set({ isPending: true });

				const newNote = await createNote(note);

				set(state => ({ ...state, data: [...state.data, newNote] }));

				return newNote;
			} finally {
				set({ isPending: false });
			}
		},
		createMany: async notes => {
			try {
				set({ isPending: true });

				const newNotes = await createManyNotes(notes);

				set(state => ({ ...state, data: [...state.data, ...newNotes] }));

				return newNotes;
			} finally {
				set({ isPending: false });
			}
		},
		createShareUrl: async id => {
			try {
				set({ isPending: true });
				const { url } = await createNoteShareUrl({ id });

				return url;
			} finally {
				set({ isPending: false });
			}
		},
		get: async id => {
			try {
				set({ isPending: true });

				const note = await getNote(id, { cache: "no-cache" });

				return note;
			} finally {
				set({ isPending: false });
			}
		},
		sync: async note => {
			try {
				set({ isPending: true });

				await syncNoteContent(note);

				set(state => {
					const updatedNote = {
						...note,
						meta: {
							...note.meta,
							updatedAt: new Date().toISOString(),
							version: note.meta.version + 1,
						},
					};

					return {
						...state,
						entity: state.entity
							? { ...state.entity, ...updatedNote }
							: state.entity,
						data: state.data.map(n =>
							n.id !== note.id
								? n
								: {
										...n,
										...updatedNote,
									}
						),
					};
				});
			} finally {
				set({ isPending: false });
			}
		},
		update: async note => {
			try {
				set({ isPending: true });

				const updatedNote = await updateNote(note);

				set(state => ({
					...state,
					entity: state.entity ? updatedNote : state.entity,
					data: state.data.map(n =>
						n.id !== updatedNote.id ? n : updatedNote
					),
				}));

				return updatedNote;
			} finally {
				set({ isPending: false });
			}
		},
		share: async (id, share) => {
			try {
				set({ isPending: true });
				const sharedNote = await shareNote(id, share);

				set(state => ({
					...state,
					entity: state.entity ? sharedNote : state.entity,
					data: state.data.map(n => (n.id !== sharedNote.id ? n : sharedNote)),
				}));
				return sharedNote;
			} finally {
				set({ isPending: false });
			}
		},
		generate: async (formData, source, onStream) => {
			const now = new Date().toISOString();
			const tempNote = {
				id: "0",
				title: "*Generating*...",
				userId: "0",
				content: [{ type: "p", children: [{ text: "" }] }],
				meta: {
					createdAt: now,
					updatedAt: now,
					version: 0,
				},
				source,
			} as NotePopulated;

			// Add temp note to the list to show the loading state
			set(state => ({
				...state,
				isPending: true,
				data: [...state.data, tempNote],
			}));

			let markdown = "";
			let imageMap: Record<string, { fileId: string; expiresAt: string }> = {};

			await generateNote(formData, source, chunk => {
				if (chunk.key === "prediction.markdown" && chunk.method === "concat") {
					markdown += chunk.value;
				}

				if (chunk.key === "prediction.images" && chunk.method === "set") {
					imageMap = chunk.value;
				}

				set(state => ({
					...state,
					data: state.data.map((n, i, ns) => {
						// Final chunk, replace the temp note with the final note
						if (i !== ns.length - 1) return n;

						const note = notePredictionStreamingReducer(
							n,
							chunk
						) as NotePopulated;

						onStream(note);

						return note;
					}),
				}));
			});

			const notes = get().data;
			let generatedNote = notes[notes.length - 1];

			if (!generatedNote) {
				throw new Error("Failed to generate note");
			}

			// Deserialize streamed markdown into PlateJS content blocks (file source only)
			if (markdown) {
				const editor = createSlateEditor({ plugins: editorPlugins });

				const deserialized = editor
					.getApi(MarkdownPlugin)
					.markdown.deserialize(markdown) as NoteBlock[];

				const enrichImages = (blocks: NoteBlock[]): NoteBlock[] =>
					blocks.map(block => {
						if (block.type === KEYS.img && block.url) {
							const meta = imageMap[block.url as string];
							if (meta) {
								return {
									...block,
									id: meta.fileId,
									expiresAt: meta.expiresAt,
								} as NoteBlock;
							}
						}

						if (block.children) {
							return {
								...block,
								children: enrichImages(block.children as NoteBlock[]),
							} as NoteBlock;
						}

						return block;
					});

				const content = enrichImages(deserialized);

				generatedNote = { ...generatedNote, content };

				set(state => ({
					...state,
					data: state.data.map((n, i, ns) =>
						i !== ns.length - 1 ? n : (generatedNote as NotePopulated)
					),
				}));
			}

			const newNote = await createNote(generatedNote);

			set(state => ({
				...state,
				isPending: false,
				data: state.data.map(n => (n.id !== newNote.id ? n : newNote)),
			}));

			return newNote;
		},
		refresh: async url => {
			try {
				set({ isPending: true });

				const notes = await fetchNotes(url, { cache: "no-cache" });

				set({ data: notes });

				return notes;
			} finally {
				set({ isPending: false });
			}
		},
		modify: async note => {
			try {
				const existingNote = get().data.find(n => n.id === note.id);

				if (!existingNote) throw new Error("Note was not found");

				set({ isPending: true });

				if ("folderId" in note) {
					// Prevent updating folder if it's the same
					if (existingNote.folderId === note.folderId) return existingNote;

					const updatedNote = await modifyNoteFolder(note);

					set(state => ({
						...state,
						entity: state.entity ? updatedNote : state.entity,
						data: state.data.map(n =>
							n.id !== updatedNote.id ? n : updatedNote
						),
					}));

					return updatedNote;
				}

				if ("title" in note) {
					// Prevent updating title if it's the same
					if (existingNote.title === note.title) return existingNote;

					const updatedNote = await modifyNoteTitle(note);

					set(state => ({
						...state,
						entity: state.entity ? updatedNote : state.entity,
						data: state.data.map(n =>
							n.id !== updatedNote.id ? n : updatedNote
						),
					}));

					return updatedNote;
				}

				throw new Error("Cannot modify note with invalid payload");
			} finally {
				set({ isPending: false });
			}
		},
		remove: async id => {
			try {
				set({ isPending: true });

				await deleteNote(id);

				const [[deletedNote], restOfNotes] = partition(
					get().data,
					n => n.id === id
				);

				set({ data: restOfNotes, entity: undefined });

				return deletedNote as NotePopulated;
			} finally {
				set({ isPending: false });
			}
		},
		edit: (id, cb) => {
			cb?.(id);

			set(state => ({
				...state,
				entity: state.data.find(n => n.id === id),
			}));
		},
		reset: () => set({ ...defaultState }),
	}));
